<template>
  <AiEducationLayout
    ><div class="overview" v-loading="loading">
      <header class="page-head">
        <div><h1>AI 教育概览</h1><p>学校教学、课程运营与 AI 治理的统一工作台</p></div>
        <div class="filters">
          <el-select v-model="schoolId" clearable placeholder="全部学校" @change="loadOverview">
            <el-option v-for="item in schools" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
          <el-button :loading="loading" @click="loadOverview"><Icon icon="ep:refresh" /></el-button>
        </div>
      </header>

      <el-row :gutter="16" class="metrics">
        <el-col v-for="item in metrics" :key="item.label" :xs="12" :sm="8" :lg="4">
          <div class="metric">
            <span class="icon-box" :class="item.tone"><Icon :icon="item.icon" :size="20" /></span>
            <span class="label">{{ item.label }}</span
            ><strong>{{ item.value }}</strong
            ><small>{{ item.note }}</small>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16" class="main-row">
        <el-col :xs="24" :lg="15">
          <section class="panel learning-panel">
            <div class="panel-head"
              ><div><h2>教学运行</h2><p>学习、作业与 AI 使用核心指标</p></div
              ><el-button
                link
                type="primary"
                @click="go('/ai-Application-center/ai-education/analytics')"
                >查看学情</el-button
              ></div
            >
            <div class="learning-grid">
              <div v-for="item in learningMetrics" :key="item.label"
                ><span>{{ item.label }}</span
                ><strong>{{ item.value }}</strong
                ><small>{{ item.unit }}</small></div
              >
            </div>
            <div class="quality-grid">
              <div
                ><span
                  >答题正确率 <b>{{ percent(analytics.correctRate) }}</b></span
                ><el-progress
                  :percentage="rate(analytics.correctRate)"
                  :show-text="false"
                  color="#16846b"
              /></div>
              <div
                ><span
                  >AI 拒答率 <b>{{ percent(analytics.aiRefusalRate) }}</b></span
                ><el-progress
                  :percentage="rate(analytics.aiRefusalRate)"
                  :show-text="false"
                  color="#c77b19"
              /></div>
            </div>
          </section>
        </el-col>
        <el-col :xs="24" :lg="9">
          <section class="panel attention-panel">
            <div class="panel-head"
              ><div><h2>待处理事项</h2><p>需要教师或运营人员跟进</p></div></div
            >
            <button
              v-for="item in attentions"
              :key="item.label"
              class="attention"
              @click="go(item.path)"
            >
              <span class="icon-box" :class="item.tone"><Icon :icon="item.icon" /></span>
              <span
                ><b>{{ item.label }}</b
                ><small>{{ item.note }}</small></span
              ><strong>{{ item.value }}</strong
              ><Icon icon="ep:arrow-right" />
            </button>
          </section>
        </el-col>
      </el-row>

      <section class="panel">
        <div class="panel-head"
          ><div><h2>常用工作入口</h2><p>按业务闭环快速进入对应工作区</p></div></div
        >
        <div class="shortcuts">
          <button v-for="item in shortcuts" :key="item.title" @click="go(item.path)">
            <span class="icon-box" :class="item.tone"><Icon :icon="item.icon" :size="21" /></span>
            <span
              ><b>{{ item.title }}</b
              ><small>{{ item.desc }}</small></span
            ><Icon icon="ep:arrow-right" />
          </button>
        </div>
      </section>
    </div>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import * as Api from '@/api/aiEducation'
import AiEducationLayout from './layout/AiEducationLayout.vue'
import { useEducationWorkspace } from './composables/useEducationWorkspace'

defineOptions({ name: 'AiEducationOverview' })
const router = useRouter()
const { workspace, initialize } = useEducationWorkspace()
const loading = ref(false)
const schoolId = ref<number>()
const schools = ref<Api.SchoolVO[]>([])
const packages = ref<Api.CoursePackageVO[]>([])
const classes = ref<Api.TeachingClassVO[]>([])
const assignments = ref<Api.AssignmentVO[]>([])
const policies = ref<Api.AiPolicyVO[]>([])
const feedback = ref<Api.AiFeedbackVO[]>([])
const drafts = ref<Api.ContentDraftVO[]>([])
const resources = ref<Api.ResourceTaskVO[]>([])
const pilots = ref<any[]>([])
const analytics = ref<Api.AnalyticsSummaryVO>({
  studySeconds: 0,
  completedChapters: 0,
  quizAttempts: 0,
  correctAnswers: 0,
  wrongAnswers: 0,
  assignmentsSubmitted: 0,
  aiRequests: 0,
  aiRefusals: 0,
  correctRate: 0,
  aiRefusalRate: 0
})

const metrics = computed(() => [
  {
    label: '学校',
    value: schoolId.value ? 1 : schools.value.length,
    note: '已接入平台',
    icon: 'ep:school',
    tone: 'blue'
  },
  {
    label: '教学班',
    value: classes.value.length,
    note: '当前范围',
    icon: 'ep:user-filled',
    tone: 'green'
  },
  {
    label: '课程包',
    value: packages.value.length,
    note: `${packages.value.filter((v) => [10, 20].includes(Number(v.status))).length} 个已发布`,
    icon: 'ep:collection',
    tone: 'cyan'
  },
  {
    label: '作业',
    value: assignments.value.length,
    note: `${assignments.value.filter((v) => [10, 20].includes(Number(v.status))).length} 个已发布`,
    icon: 'ep:document-checked',
    tone: 'amber'
  },
  {
    label: 'AI 策略',
    value: policies.value.filter((v) => v.enabled !== false).length,
    note: '当前启用',
    icon: 'ep:lock',
    tone: 'purple'
  },
  { label: '试点项目', value: pilots.value.length, note: '持续运营', icon: 'ep:flag', tone: 'red' }
])
const learningMetrics = computed(() => [
  {
    label: '累计学习',
    value: Math.round((analytics.value.studySeconds || 0) / 3600),
    unit: '小时'
  },
  { label: '完成章节', value: analytics.value.completedChapters || 0, unit: '章' },
  { label: '测验次数', value: analytics.value.quizAttempts || 0, unit: '次' },
  { label: '作业提交', value: analytics.value.assignmentsSubmitted || 0, unit: '份' },
  { label: 'AI 问答', value: analytics.value.aiRequests || 0, unit: '次' }
])
const attentions = computed(() => [
  {
    label: '教师跟进',
    note: '学生转交的 AI 问题',
    value: feedback.value.length,
    icon: 'ep:chat-dot-round',
    tone: 'amber',
    path: '/ai-Application-center/ai-education/learning'
  },
  {
    label: '内容审核',
    note: '等待审核的 AI 草稿',
    value: drafts.value.filter((v) => v.status === 20).length,
    icon: 'ep:stamp',
    tone: 'purple',
    path: '/ai-Application-center/ai-education/operations'
  },
  {
    label: '资源异常',
    note: '解析或向量化失败',
    value: resources.value.filter((v) => v.status === 90).length,
    icon: 'ep:warning',
    tone: 'red',
    path: '/ai-Application-center/ai-education/operations'
  }
])
const shortcuts = [
  {
    title: '基础配置',
    desc: '学校、班级、课程包与成员',
    icon: 'ep:setting',
    tone: 'blue',
    path: '/ai-Application-center/ai-education/configuration'
  },
  {
    title: '教学与 AI 治理',
    desc: '作业、知识库和受控问答',
    icon: 'ep:reading',
    tone: 'green',
    path: '/ai-Application-center/ai-education/learning'
  },
  {
    title: '学情分析',
    desc: '学习质量、效果与报告',
    icon: 'ep:data-analysis',
    tone: 'cyan',
    path: '/ai-Application-center/ai-education/analytics'
  },
  {
    title: '内容与运营',
    desc: '内容审核、资源和试点',
    icon: 'ep:operation',
    tone: 'purple',
    path: '/ai-Application-center/ai-education/operations'
  }
]

function pick<T>(result: PromiseSettledResult<T>, fallback: T): T {
  return result.status === 'fulfilled' ? result.value : fallback
}
const loadOverview = async () => {
  loading.value = true
  try {
    if (!schools.value.length) {
      schools.value =
        (await Api.getSchoolOptions().catch(() => [])) ||
        (await Api.getSchoolList().catch(() => [])) ||
        []
    }
    const r = await Promise.allSettled([
      Api.getCoursePackageList(),
      Api.getTeachingClassList({ schoolId: schoolId.value }),
      Api.getAssignmentList(),
      Api.getAiPolicyList(schoolId.value),
      Api.getEscalatedAiFeedbackList(10),
      Api.getContentDraftList({ status: 20 }),
      Api.getResourceTaskList(),
      Api.getPilotProjectList(schoolId.value),
      Api.getAnalyticsSummary()
    ])
    packages.value = pick(r[0], []) as Api.CoursePackageVO[]
    classes.value = pick(r[1], []) as Api.TeachingClassVO[]
    assignments.value = pick(r[2], []) as Api.AssignmentVO[]
    policies.value = pick(r[3], []) as Api.AiPolicyVO[]
    feedback.value = pick(r[4], []) as Api.AiFeedbackVO[]
    drafts.value = pick(r[5], []) as Api.ContentDraftVO[]
    resources.value = pick(r[6], []) as Api.ResourceTaskVO[]
    pilots.value = pick(r[7], []) as any[]
    analytics.value = pick(r[8], analytics.value) as Api.AnalyticsSummaryVO
  } finally {
    loading.value = false
  }
}
const rate = (value?: number) =>
  Math.min(100, Math.max(0, Number(value || 0) * (Number(value || 0) <= 1 ? 100 : 1)))
const percent = (value?: number) => `${rate(value).toFixed(1)}%`
const go = (path: string) => router.push(path)
onMounted(async () => {
  try {
    await initialize()
    const ws = workspace.value?.workspace
    // 教师默认进工作台，避免概览页无权限接口刷 403
    if (ws === 'teacher') {
      await router.replace('/ai-Application-center/ai-education/teacher')
      return
    }
  } catch {
    /* ignore */
  }
  await loadOverview()
})
</script>

<style scoped>
.overview {
  min-height: 100%;
  padding: 20px;
  color: #182230;
  background: #f5f7fa;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.page-head h1,
.panel h2 {
  margin: 0;
  letter-spacing: 0;
}

.page-head h1 {
  font-size: 22px;
}

.page-head p,
.panel-head p {
  margin: 5px 0 0;
  font-size: 13px;
  color: #7a8594;
}

.filters {
  display: flex;
  gap: 8px;
}

.filters .el-select {
  width: 210px;
}

.metrics,
.main-row {
  margin-bottom: 16px;
}

.metric,
.panel {
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  box-sizing: border-box;
}

.metric {
  min-height: 136px;
  padding: 17px;
}

.icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

.metric .label,
.metric strong,
.metric small {
  display: block;
}

.metric .label {
  margin-top: 12px;
  font-size: 13px;
  color: #667180;
}

.metric strong {
  margin-top: 2px;
  font-size: 26px;
}

.metric small {
  color: #98a1ad;
}

.panel {
  padding: 20px;
}

.main-row .panel {
  min-height: 300px;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.panel h2 {
  font-size: 16px;
}

.learning-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  border: 1px solid #edf0f4;
}

.learning-grid > div {
  padding: 17px 14px;
  border-right: 1px solid #edf0f4;
}

.learning-grid > div:last-child {
  border: 0;
}

.learning-grid span,
.learning-grid strong {
  display: block;
}

.learning-grid span {
  font-size: 12px;
  color: #74808f;
}

.learning-grid strong {
  display: inline-block;
  margin-top: 8px;
  font-size: 22px;
}

.learning-grid small {
  margin-left: 4px;
  color: #98a1ad;
}

.quality-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 25px;
}

.quality-grid span {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.attention {
  display: grid;
  width: 100%;
  padding: 12px 4px;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-top: 1px solid #edf0f4;
  grid-template-columns: 36px 1fr auto 16px;
  align-items: center;
  gap: 10px;
}

.attention b,
.attention small,
.shortcuts b,
.shortcuts small {
  display: block;
}

.attention small,
.shortcuts small {
  margin-top: 3px;
  font-size: 12px;
  color: #8b95a2;
}

.shortcuts {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.shortcuts button {
  display: grid;
  padding: 14px;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
  grid-template-columns: 42px 1fr 16px;
  align-items: center;
  gap: 11px;
}

.attention:hover,
.shortcuts button:hover {
  background: #f8fafc;
}

.blue {
  color: #2673dd;
  background: #eaf2ff;
}

.green {
  color: #16846b;
  background: #e7f7f2;
}

.cyan {
  color: #16809a;
  background: #e8f6f9;
}

.amber {
  color: #b56c10;
  background: #fff3df;
}

.purple {
  color: #7656b5;
  background: #f1ecfb;
}

.red {
  color: #c54d4d;
  background: #fdecec;
}

@media (width <= 992px) {
  .main-row .el-col + .el-col {
    margin-top: 16px;
  }

  .shortcuts {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (width <= 640px) {
  .overview {
    padding: 12px;
  }

  .filters .el-select {
    width: 150px;
  }

  .learning-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .quality-grid,
  .shortcuts {
    grid-template-columns: 1fr;
  }
}
</style>
